import { defineStore } from 'pinia'
import { computed, nextTick, ref } from 'vue'

import { DateEntryLine, TimesheetLine } from '@/components/Timesheet/types'
import { CustomFieldTypeValueInput } from '@/gql/graphql'
import { useTimesheetFormStore } from '@/components/Timesheet/store/useTimesheetFormStore'
import { useFieldableFormStore } from '@/components/Fieldable/store/useFieldableFormStore'
import { useTranslation } from '@/composables/translation'

/*
 * This store is responsible for managing the timesheet lines.
 * Timesheet lines are not their own entity in the database, but are a collection
 * of timesheet_registrations which have a common title, and additional attributes like is_payable, and custom fields.
 * They are identified by the title of the line.
 */
export const useTimesheetLinesStore = defineStore('timesheet-lines-store', () => {
  const timesheetLines = ref<TimesheetLine[]>([])
  const timesheetFormStore = useTimesheetFormStore()
  const fieldableFormStore = useFieldableFormStore()
  const { $t } = useTranslation()

  // Display the modal for adding or editing a timesheet line
  const isOpenModalToAddOrEditLine = ref(false)
  const titleForModalAddEditLine = ref($t('Add a new timesheet line'))
  const descriptionForModalAddEditLine = ref(
    $t('This will create a row in your timesheet which can be then used to assign worked time.'),
  )

  // When staging a line for edits, we store the initial state prior to editing
  const initialStateOfStagedTimesheetLine = ref<TimesheetLine | null>(null)
  // The state of the timesheet line controlled by the add/edit modal
  const liveStateOfStagedTimesheetLine = ref<TimesheetLine>({
    customFieldValues: [],
    isPayable: true,
    amounts: [],
    total: 0,
  })

  const resetStagedTimesheetLine = () => {
    initialStateOfStagedTimesheetLine.value = null
    liveStateOfStagedTimesheetLine.value = {
      customFieldValues: [],
      isPayable: true,
      amounts: [],
      total: 0,
    }
  }

  const closeModalToAddOrEditLine = () => {
    isOpenModalToAddOrEditLine.value = false
    resetStagedTimesheetLine()
  }

  const openModalToAddNewLine = () => {
    resetStagedTimesheetLine()
    titleForModalAddEditLine.value = $t('Add a new timesheet line')
    descriptionForModalAddEditLine.value = $t(
      'This will create a row in your timesheet which can be then used to assign worked time.',
    )
    isOpenModalToAddOrEditLine.value = true
  }

  // We currently use the selected custom fields to derive a title for the timesheet line.
  const stagedTimesheetLineWithDynamicProperties = computed(() => ({
    ...liveStateOfStagedTimesheetLine.value,
    title: fieldableFormStore.selectedValues.join(', '),
  }))

  const openModalToEditLine = (timesheetLineTitle: string) => {
    if (timesheetFormStore.isViewOnly) {
      return
    }
    titleForModalAddEditLine.value = $t('Edit timesheet line')
    descriptionForModalAddEditLine.value = null
    const existingTimesheetLine = {
      ...timesheetLines.value.find((line) => line.title === timesheetLineTitle),
    }
    if (!existingTimesheetLine) {
      return
    }
    // Make a copy of the line that is staged for changes
    initialStateOfStagedTimesheetLine.value = {
      ...(existingTimesheetLine as TimesheetLine),
    }
    // Set up the line for editing
    liveStateOfStagedTimesheetLine.value = {
      ...(existingTimesheetLine as TimesheetLine),
    }
    isOpenModalToAddOrEditLine.value = true
  }

  const commitStagedTimesheetLine = async () => {
    if (stagedTimesheetLineWithDynamicProperties.value?.title === '') {
      return
    }

    // When making updates to existing timesheet line
    if (initialStateOfStagedTimesheetLine.value) {
      // Find the existing timesheet line that we need to make changes to
      const existingTimesheetLine = timesheetLines.value.find(
        (line) => line.title === initialStateOfStagedTimesheetLine.value?.title,
      )
      if (!existingTimesheetLine) {
        return
      }
      // Assign the updated properties to the existing line (not deep)
      // Update the custom field values of all of the timesheet line registrations
      Object.keys(timesheetFormStore.weekRange).forEach((weekNumber) => {
        timesheetFormStore.weekRange[weekNumber].dates.forEach((date) => {
          const dateString = date.format('YYYY-MM-DD')
          Object.keys(timesheetFormStore.tableDateEntries[dateString].lines).forEach(
            (lineTitle) => {
              if (lineTitle === initialStateOfStagedTimesheetLine.value?.title) {
                const newLineTitle = stagedTimesheetLineWithDynamicProperties.value?.title
                const oldLineTitle = lineTitle

                timesheetFormStore.tableDateEntries[dateString].lines[newLineTitle] = {
                  ...timesheetFormStore.tableDateEntries[dateString].lines[oldLineTitle],
                }
              }
            },
          )
        })
      })
      Object.assign(existingTimesheetLine, stagedTimesheetLineWithDynamicProperties.value)
    } else {
      // Ensure there is not already an existing timesheet line with the same title
      if (
        timesheetLines.value.find(
          (line) => line.title === stagedTimesheetLineWithDynamicProperties.value?.title,
        )
      ) {
        return
      }

      //   When commiting a new line
      timesheetLines.value.push({ ...stagedTimesheetLineWithDynamicProperties.value })
      await nextTick()
      // Initialize the line with a duration of 0 for each date
      Object.keys(timesheetFormStore.weekRange).forEach((weekNumber) => {
        timesheetFormStore.weekRange[weekNumber].dates.forEach((date) => {
          const dateString = date.format('YYYY-MM-DD')
          if (!timesheetFormStore.tableDateEntries?.[dateString]) {
            timesheetFormStore.tableDateEntries[dateString] = {}
          }
          if (!timesheetFormStore.tableDateEntries[dateString]?.lines) {
            timesheetFormStore.tableDateEntries[dateString].lines = {}
          }

          const blankTimesheetLinesForTableEntries = timesheetLines.value.reduce(
            (acc: { [key: string]: DateEntryLine }, line: TimesheetLine) => {
              if (line.title) {
                acc[line.title] = { duration: 0 }
              }
              return acc
            },
            {},
          )

          // Set a default of 0 for each timesheet line which does not already have a duration
          timesheetFormStore.tableDateEntries[dateString].lines = {
            ...blankTimesheetLinesForTableEntries,
            ...timesheetFormStore.tableDateEntries[dateString].lines,
          }
        })
      })
    }
    // Since timesheet lines may have changed, we make sure to remove any
    // registrations related to those lines which are no longer available
    const timesheetLinesTitles = timesheetLines.value.map((line) => line.title)
    Object.keys(timesheetFormStore.tableDateEntries).forEach((date) => {
      timesheetFormStore.tableDateEntries[date].lines = Object.fromEntries(
        Object.entries(timesheetFormStore.tableDateEntries[date].lines).filter(([key]) =>
          timesheetLinesTitles.includes(key),
        ),
      )
    })
  }

  const deleteStagedTimesheetLine = async () => {
    timesheetLines.value.splice(
      0,
      timesheetLines.value.length,
      ...timesheetLines.value.filter(
        (line) => line.title !== initialStateOfStagedTimesheetLine.value?.title,
      ),
    )
    resetStagedTimesheetLine()
  }

  const addLine = async ({ title, customFieldValues, isPayable }) => {
    if (customFieldValues.length && !timesheetLines.value.find((line) => line.title === title)) {
      timesheetLines.value.push({
        title,
        isPayable,
        customFieldValues: customFieldValues as CustomFieldTypeValueInput[],
        amounts: [],
        total: 0,
      })
    }

    await nextTick()
    // Initialize the line with a duration of 0 for each date
    Object.keys(timesheetFormStore.weekRange).forEach((weekNumber) => {
      timesheetFormStore.weekRange[weekNumber].dates.forEach((date) => {
        const dateString = date.format('YYYY-MM-DD')
        if (!timesheetFormStore.tableDateEntries?.[dateString]) {
          timesheetFormStore.tableDateEntries[dateString] = {}
        }
        if (!timesheetFormStore.tableDateEntries[dateString]?.lines) {
          timesheetFormStore.tableDateEntries[dateString].lines = {}
        }

        const blankTimesheetLinesForTableEntries = timesheetLines.value.reduce(
          (acc: { [key: string]: DateEntryLine }, line: TimesheetLine) => {
            if (line.title) {
              acc[line.title] = { duration: 0 }
            }
            return acc
          },
          {},
        )

        // Set a default of 0 for each timesheet line which does not already have a duration
        timesheetFormStore.tableDateEntries[dateString].lines = {
          ...blankTimesheetLinesForTableEntries,
          ...timesheetFormStore.tableDateEntries[dateString].lines,
        }
      })
    })
  }
  const sumOfTimesheetLines = computed(() =>
    Object.keys(timesheetFormStore.tableDateEntries).reduce(
      (acc, key) => {
        if (!timesheetFormStore?.tableDateEntries?.[key]?.lines) {
          return acc
        }
        acc[key] = Object.values(
          timesheetFormStore.tableDateEntries[key].lines as Record<string, DateEntryLine>,
        ).reduce<number>((sum, line) => sum + line.duration, 0)
        return acc
      },
      {} as { [key: string]: number },
    ),
  )

  const pruneLines = () => {
    const lines = timesheetLines.value
    const lineTitles = lines.map((line) => line.title || '')
    const nonZeroLinesInTable = Object.keys(timesheetFormStore.tableDateEntries).reduce(
      (acc, key) => {
        const lines = timesheetFormStore.tableDateEntries[key].lines
        Object.keys(lines).forEach((line) => {
          if (lines[line].duration > 0) {
            acc.push(line)
          }
        })
        return acc
      },
      [] as string[],
    )
    const linesToRemove = lineTitles.filter((line) => !nonZeroLinesInTable.includes(line))
    linesToRemove.forEach((lineTitle) => {
      timesheetLines.value = [...timesheetLines.value.filter((line) => line.title !== lineTitle)]
    })
  }

  return {
    timesheetLines,
    sumOfTimesheetLines,
    isOpenModalToAddOrEditLine,
    liveStateOfStagedTimesheetLine,
    titleForModalAddEditLine,
    descriptionForModalAddEditLine,
    openModalToEditLine,
    commitStagedTimesheetLine,
    closeModalToAddOrEditLine,
    openModalToAddNewLine,
    deleteStagedTimesheetLine,
    addLine,
    pruneLines,
  }
})
